import { useEffect, useState } from 'react';

import {
  ActionPermissions,
  Attachment,
  CurrentUserVisibilityRuleAccess,
  CustomPermission,
  ReportAccessResponse,
  SavedReportsList,
} from '../../../../../@types';
import { SavedReportTypeList } from '../../../../../configs/enums';
import { isUserHasPermission } from '../../../../../configs/permissions';
import { CURRENT_USER_VISIBILITY_RULE_ACCESS } from '../../../../../constants/user.constant';
import {
  useGetAttachmentTypes,
  useGetShareAccessDataByReportId,
} from '../../../../../queries/report-query';
import { useGetUserSystemPermissions } from '../../../../../queries/user-query';
import { useGetCurrentUserVisibility } from '../../../../../queries/visibilityRules-query';
import { getTabsByPermission } from '../../../../../utils/report';
import { toast } from '../../../../../utils/toast';

/**
 * Stable fallbacks, not inline literals. The Direct Share tab keys an effect off
 * `savedData`, so handing it a fresh `{ data: [] }` on every render while the
 * query is still empty re-seeds its row state in a loop.
 */
const EMPTY_SHARE_ACCESS: ReportAccessResponse = { data: [] };
const EMPTY_ATTACHMENT_TYPES: Attachment[] = [];
const EMPTY_PERMISSIONS: CustomPermission[] = [];

/**
 * Queries, permission gating and save-toast plumbing for the Share & Schedule
 * dialog, so the component itself is only chrome.
 */
export const useReportEditDialogData = (
  open: boolean,
  refetchReports: (refetch: boolean) => void,
  rowData?: SavedReportsList
) => {
  const [refetch, setRefetch] = useState(false);

  const getCurrentUserVisibility = useGetCurrentUserVisibility();
  const getUserPermissionsQuery = useGetUserSystemPermissions();
  const attachmentTypesQuery = useGetAttachmentTypes();
  const shareAccessQuery = useGetShareAccessDataByReportId(rowData?.id.toString() || '');

  useEffect(() => {
    getCurrentUserVisibility.refetch();
    getUserPermissionsQuery.refetch();
    // Mount-only kick-off for two `enabled: false` queries. Both query objects are
    // recreated on every render, so listing them would refetch in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // The id guard matters: the query is built from `rowData?.id.toString() || ''`,
    // so firing without a row would request the share list for report `''`.
    if (open && rowData?.id !== undefined) {
      shareAccessQuery.refetch();
    }
    // Refetching the share list is the only thing opening should trigger; the
    // query object identity changes every render, so it stays out of the deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rowData?.id]);

  useEffect(() => {
    if (refetch) {
      toast.success('Report saved');
      refetchReports(true);
      setRefetch(false);
    }
  }, [refetch, refetchReports]);

  /**
   * Read straight off the queries rather than mirroring them into state via an
   * effect. Mirroring bought nothing and made an update loop possible: the
   * effects keyed off `query.data`, so any consumer handing back a fresh object
   * identity per render re-set state forever.
   */
  const permissions = getUserPermissionsQuery.data || EMPTY_PERMISSIONS;
  const visibilityAccess: CurrentUserVisibilityRuleAccess =
    getCurrentUserVisibility.data || CURRENT_USER_VISIBILITY_RULE_ACCESS;

  const canShare = isUserHasPermission(ActionPermissions.Report_My_Report_Sharing, permissions);
  const canSchedule = isUserHasPermission(ActionPermissions.Report_My_Report_Schedule, permissions);
  const hasSettingsPermission = isUserHasPermission(
    ActionPermissions.Report_My_Reports_Report_Settings,
    permissions
  );
  const isPartListReport = rowData?.pageName === SavedReportTypeList.PartList;
  const hasGlobalVisibility = Boolean(visibilityAccess?.hasGlobalVisibility);

  return {
    permissions,
    canShare,
    canSchedule,
    canEditSettings: hasSettingsPermission && isPartListReport && hasGlobalVisibility,
    visibleTabs: getTabsByPermission(
      canShare,
      canSchedule,
      hasSettingsPermission,
      isPartListReport,
      hasGlobalVisibility
    ),
    attachmentTypes: attachmentTypesQuery.data?.data || EMPTY_ATTACHMENT_TYPES,
    shareAccessData: shareAccessQuery.data || EMPTY_SHARE_ACCESS,
    refetchShareAccess: shareAccessQuery.refetch,
    handleRefetch: setRefetch,
  };
};
